import { AFRICAN_COUNTRIES } from '@/data/countries';

// European countries that use EUR
const EUROPEAN_COUNTRIES = [
  'austria', 'belgium', 'cyprus', 'estonia', 'finland', 'france', 'germany',
  'greece', 'ireland', 'italy', 'latvia', 'lithuania', 'luxembourg', 'malta',
  'netherlands', 'portugal', 'slovakia', 'slovenia', 'spain', 'croatia',
  'bulgaria', 'romania', 'czech-republic', 'hungary', 'poland', 'sweden',
  'denmark', 'iceland', 'norway', 'switzerland', 'monaco', 'andorra',
  'san-marino', 'vatican-city', 'belarus', 'bosnia-and-herzegovina', 'georgia',
  'liechtenstein', 'moldova', 'montenegro', 'north-macedonia', 'serbia', 'ukraine'
];

// Latin American countries
const LATIN_AMERICAN_COUNTRIES = [
  'argentina', 'bolivia', 'brazil', 'chile', 'colombia', 'costa-rica', 'cuba',
  'dominican-republic', 'ecuador', 'el-salvador', 'guatemala', 'haiti', 'honduras',
  'jamaica', 'mexico', 'nicaragua', 'panama', 'paraguay', 'peru', 'suriname',
  'trinidad-and-tobago', 'uruguay', 'venezuela', 'guyana', 'belize', 'grenada',
  'antigua-and-barbuda', 'dominica', 'saint-kitts-and-nevis', 'saint-lucia',
  'saint-vincent-and-grenadines', 'barbados', 'bahamas'
];

// Asian countries (including Middle East)
const ASIAN_COUNTRIES = [
  'afghanistan', 'armenia', 'azerbaijan', 'bahrain', 'bangladesh', 'bhutan',
  'brunei', 'cambodia', 'china', 'east-timor', 'georgia', 'india', 'indonesia',
  'iran', 'iraq', 'israel', 'japan', 'jordan', 'kazakhstan', 'kuwait', 'kyrgyzstan',
  'laos', 'lebanon', 'malaysia', 'maldives', 'mongolia', 'myanmar', 'nepal',
  'north-korea', 'oman', 'pakistan', 'philippines', 'qatar', 'russia', 'saudi-arabia',
  'singapore', 'south-korea', 'sri-lanka', 'syria', 'taiwan', 'tajikistan', 'thailand',
  'turkmenistan', 'turkey', 'united-arab-emirates', 'uzbekistan', 'vietnam', 'yemen'
];

// Get region for a country
export function getRegionForCountry(countryId: string): 'europe' | 'non-european' | 'africa' | 'latin-america' | 'asia' {
  const normalizedId = countryId.toLowerCase();
  
  // Check in priority order: Europe, Africa, Latin America, Asia
  if (EUROPEAN_COUNTRIES.includes(normalizedId)) {
    return 'europe';
  }
  
  if (AFRICAN_COUNTRIES.some(country => country.id === normalizedId)) {
    return 'africa';
  }
  
  if (LATIN_AMERICAN_COUNTRIES.includes(normalizedId)) {
    return 'latin-america';
  }
  
  if (ASIAN_COUNTRIES.includes(normalizedId)) {
    return 'asia';
  }
  
  // Default to non-european for remaining countries (North America, Oceania, etc.)
  return 'non-european';
}

// Get currency for a region
function getCurrencyForRegion(region: 'europe' | 'non-european' | 'africa' | 'latin-america' | 'asia'): 'EUR' | 'USD' {
  return region === 'europe' ? 'EUR' : 'USD';
}

// Salary range interface
export interface SalaryRange {
  value: string;
  label: string;
}

// Get salary ranges for a region and role level based on Salary.md specifications
// Returns 3 ranges: the selected role level plus adjacent levels
export function getSalaryRanges(
  region: 'europe' | 'non-european' | 'africa' | 'latin-america' | 'asia',
  roleLevel: 'junior' | 'mid' | 'senior' | 'staff' | 'principal'
): SalaryRange[] {
  // Define all ranges by region and role level
  const allRangesByRegion: Record<string, Record<string, SalaryRange>> = {
    europe: {
      junior: { value: '35k-50k', label: '35k to 50k EUR' },
      mid: { value: '50k-70k', label: '50k to 70k EUR' },
      senior: { value: '70k-100k', label: '70k to 100k EUR' },
      staff: { value: '100k-150k', label: '100k to 150k EUR' },
      principal: { value: '150k-plus', label: '150k+ EUR' },
    },
    'non-european': {
      junior: { value: '50k-70k', label: '50k to 70k USD' },
      mid: { value: '60k-90k', label: '60k to 90k USD' },
      senior: { value: '90k-130k', label: '90k to 130k USD' },
      staff: { value: '150k-200k', label: '150k to 200k USD' },
      principal: { value: '200k-plus', label: '200k+ USD' },
    },
    africa: {
      junior: { value: '15k-25k', label: '15k to 25k USD' },
      mid: { value: '25k-45k', label: '25k to 45k USD' },
      senior: { value: '45k-70k', label: '45k to 70k USD' },
      staff: { value: '70k-110k', label: '70k to 110k USD' },
      principal: { value: '110k-plus', label: '110k+ USD' },
    },
    'latin-america': {
      junior: { value: '18k-30k', label: '18k to 30k USD' },
      mid: { value: '30k-50k', label: '30k to 50k USD' },
      senior: { value: '50k-80k', label: '50k to 80k USD' },
      staff: { value: '80k-120k', label: '80k to 120k USD' },
      principal: { value: '120k-plus', label: '120k+ USD' },
    },
    asia: {
      junior: { value: '12k-25k', label: '12k to 25k USD' },
      mid: { value: '20k-40k', label: '20k to 40k USD' },
      senior: { value: '40k-65k', label: '40k to 65k USD' },
      staff: { value: '65k-100k', label: '65k to 100k USD' },
      principal: { value: '100k-plus', label: '100k+ USD' },
    },
  };
  
  const regionRanges = allRangesByRegion[region];
  if (!regionRanges) return [];
  
  // Role level order for determining adjacent levels
  const roleOrder: Array<'junior' | 'mid' | 'senior' | 'staff' | 'principal'> = 
    ['junior', 'mid', 'senior', 'staff', 'principal'];
  
  const currentIndex = roleOrder.indexOf(roleLevel);
  if (currentIndex === -1) return [];
  
  // Get 3 ranges: current level plus adjacent levels
  const result: SalaryRange[] = [];
  
  // Add one level below (if exists and we need more ranges)
  if (currentIndex > 0 && result.length < 3) {
    const lowerLevel = roleOrder[currentIndex - 1];
    if (regionRanges[lowerLevel]) {
      result.push(regionRanges[lowerLevel]);
    }
  }
  
  // Add current level
  if (regionRanges[roleLevel]) {
    result.push(regionRanges[roleLevel]);
  }
  
  // Add one level above (if exists and we need more ranges)
  if (currentIndex < roleOrder.length - 1 && result.length < 3) {
    const higherLevel = roleOrder[currentIndex + 1];
    if (regionRanges[higherLevel]) {
      result.push(regionRanges[higherLevel]);
    }
  }
  
  // Ensure we always return at least the current level's range
  if (result.length === 0 && regionRanges[roleLevel]) {
    result.push(regionRanges[roleLevel]);
  }
  
  return result;
}

// Legacy function for backward compatibility (if needed)
export function getCurrencyForCountry(countryId: string): 'EUR' | 'USD' {
  const region = getRegionForCountry(countryId);
  return getCurrencyForRegion(region);
}